// ─────────────────────────────────────────────────────────────────────────────
// WP-15 REQ-134 — Modal de edición de una insignia de Gestión. Reemplaza el bloque expandido
// inline que antes vivía suelto en AdminGestion.tsx: nombre ×7 (con traductor), semanas, color con
// selector nativo, ícono (carga real, no URL de texto), activa/inactiva y vista previa.
//
// El "Guardar cambios" de este modal solo actualiza el draft del padre (AdminGestion.patchBadge) y
// cierra — no llama al backend. El único guardado real de texto/color/semanas/activo sigue siendo
// el botón "Guardar" atómico de la pantalla. La ÚNICA operación que persiste al instante es la carga
// de imagen (mismo patrón ya usado en Catálogo: Fundamentales/Actitudes/Hábitos).
//
// Insignia nueva (`isNew`): el selector de imagen se deshabilita hasta el primer Guardar general,
// porque subir requiere un `id` ya persistido en `badges_config` (POST .../badges/{id}/image).
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter, CatalogImageUploadField } from '@/components/common';
import { useUploadBadgeImage, getRecognitionErrorMessage, getBadgeName } from '@/api/recognition';
import type { AdminBadgeDto, LocalizedText } from '@/api/recognition';
import { AdminToggle, LocalizedFields, hasAllLanguages } from './adminShared';
import { weeksKeyOf } from '@/utils/weeksPlural';
import { asset } from '@/utils/basePath';

const ADMIN_WEEKS_KEYS = {
  one: 'admin.management.weeksRequiredOne',
  few: 'admin.management.weeksRequired',
  many: 'admin.management.weeksRequiredMany',
} as const;

const TITLE_ID = 'adm-badge-editor-title';

interface AdminBadgeEditorModalProps {
  badge: AdminBadgeDto | null;
  /** true si la insignia todavía no pasó por un Guardar general (sin id persistido en `badges_config`). */
  isNew: boolean;
  onSave: (badge: AdminBadgeDto) => void;
  /** Se dispara tras una carga de ícono exitosa — actualiza el draft del padre SIN cerrar el modal
   *  (distinto de onSave, que es el path "guardar y cerrar" del botón "Guardar cambios"). */
  onIconUploaded: (badge: AdminBadgeDto) => void;
  onClose: () => void;
}

export function AdminBadgeEditorModal({
  badge, isNew, onSave, onIconUploaded, onClose,
}: Readonly<AdminBadgeEditorModalProps>) {
  const { t, i18n } = useTranslation('recognition');
  const uploadImage = useUploadBadgeImage();
  const [draft, setDraft] = useState<AdminBadgeDto | null>(badge);
  const [uploadError, setUploadError] = useState<string | null>(null);

  if (draft?.id !== badge?.id) setDraft(badge);
  if (!draft) return null;

  const patch = (patchValues: Partial<AdminBadgeDto>) =>
    setDraft((current) => (current ? { ...current, ...patchValues } : current));

  const handleUpload = async (file: File) => {
    setUploadError(null);
    try {
      const updated = await uploadImage.mutateAsync({ badgeId: draft.id, file });
      // La carga ya persistió en el backend (mismo patrón que Catálogo) — propaga el iconUrl al
      // draft del PADRE de inmediato, no solo al local. Si el admin cierra el modal con
      // Cancelar/X/Esc sin pulsar "Guardar cambios", el siguiente Guardar general de AdminGestion
      // debe ver el iconUrl nuevo y no el viejo (o null), o el ícono recién subido se perdería.
      // onIconUploaded (no onSave) porque esto no es un "guardar y cerrar": el admin sigue editando.
      patch({ iconUrl: updated.iconUrl });
      onIconUploaded({ ...draft, iconUrl: updated.iconUrl });
    } catch (error) {
      setUploadError(getRecognitionErrorMessage(error, t('admin.management.iconFormatInvalid')));
    }
  };

  const weeksLabel = typeof draft.semanasRequeridas === 'number'
    ? t(weeksKeyOf(draft.semanasRequeridas, ADMIN_WEEKS_KEYS), { count: draft.semanasRequeridas })
    : '';

  return (
    <Modal open={badge !== null} onClose={onClose} maxWidth={640} labelledBy={TITLE_ID}>
      <ModalHeader onClose={onClose}>
        <h2 id={TITLE_ID}>{t('admin.management.editBadge')} — {draft.code}</h2>
      </ModalHeader>
      <ModalBody>
        <LocalizedFields
          label={t('admin.management.badgeName')}
          value={draft.nombre}
          onChange={(nombre: LocalizedText) => patch({ nombre })}
          requireAll
          translatable
        />

        <div className="adm-form-grid">
          <label className="adm-field"><span>{t('admin.management.weeks')}</span>
            <input type="number" min={0} value={draft.semanasRequeridas ?? ''}
              onChange={(event) => patch({ semanasRequeridas: event.target.value ? Number(event.target.value) : null })} />
          </label>
          <label className="adm-field"><span>{t('admin.management.badgeColor')}</span>
            <div className="adm-color-field">
              <input type="color" value={draft.color ?? '#342F82'} onChange={(event) => patch({ color: event.target.value })} />
              <input type="text" value={draft.color ?? ''} placeholder={t('admin.management.notApplicable')}
                onChange={(event) => patch({ color: event.target.value || null })} />
            </div>
          </label>
        </div>

        <CatalogImageUploadField
          label={t('admin.management.badgeIcon')}
          currentImageUrl={draft.iconUrl ?? undefined}
          onUpload={handleUpload}
          isUploading={uploadImage.isPending}
          disabled={isNew}
        />
        {isNew && <p className="adm-note">{t('admin.management.imageUploadHintNew')}</p>}
        {uploadError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {uploadError}</p>}

        <label className="adm-field adm-field--toggle">
          <span>{draft.isActive ? t('admin.management.badgeActive') : t('admin.management.badgeInactive')}</span>
          <AdminToggle isOn={draft.isActive} onChange={(isActive) => patch({ isActive })} />
        </label>

        {!hasAllLanguages(draft.nombre) && (
          <p className="adm-note adm-note--warn"><i className="fas fa-language" /> {t('admin.management.needAllLangs')}</p>
        )}

        <div className="adm-badge-preview">
          <p className="adm-badge-preview-title">{t('admin.management.previewTitle')}</p>
          <div className="rp2-insignia-item">
            <img src={draft.iconUrl || asset('/color-icon.png')} alt="" className="rp2-insignia-img" />
            <div className="rp2-insignia-meta">
              <span className="rp2-insignia-name">{getBadgeName(draft, i18n.language)}</span>
              {weeksLabel && <span className="rp2-insignia-year">{weeksLabel}</span>}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn--primary" onClick={() => onSave(draft)}>
          <i className="fas fa-check" /> {t('admin.management.saveBadgeChanges')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

export default AdminBadgeEditorModal;
